const RESULT_FIELDS = [
  "status",
  "score",
  "nbrOfAttempts",
  "focusLostCount",
  "lastRun",
  "showResult",
];

const STATUS_BUCKET_BY_ACCENT = {
  success: "passed",
  danger: "failed",
  warning: "inProgress",
  secondary: "notStarted",
};

function isRootRow(row) {
  return row.parentId == null;
}

function groupChildrenByParent(rows) {
  const childrenByParent = new Map();
  rows.forEach((row) => {
    if (isRootRow(row)) return;
    if (!childrenByParent.has(row.parentId)) {
      childrenByParent.set(row.parentId, []);
    }
    childrenByParent.get(row.parentId).push(row);
  });
  return childrenByParent;
}

function buildRowIndex(rows = []) {
  const byId = new Map();
  rows.forEach((row) => byId.set(row.id, row));
  return { byId, childrenByParent: groupChildrenByParent(rows) };
}

function collectDescendants(id, childrenByParent) {
  const result = [];
  const visited = new Set([id]);
  const stack = [...(childrenByParent.get(id) ?? [])];

  while (stack.length > 0) {
    const row = stack.pop();
    if (visited.has(row.id)) continue;

    visited.add(row.id);
    result.push(row);
    stack.push(...(childrenByParent.get(row.id) ?? []));
  }

  return result;
}

function collectAncestorIds(row, byId) {
  const ancestors = [];
  const visited = new Set([row.id]);
  let current = row.parentId != null ? byId.get(row.parentId) : null;

  while (current != null && !visited.has(current.id)) {
    visited.add(current.id);
    ancestors.push(current.id);
    current = current.parentId != null ? byId.get(current.parentId) : null;
  }

  return ancestors;
}

function summariseExams(exams) {
  return exams.reduce(
    (summary, exam) => {
      const bucket = STATUS_BUCKET_BY_ACCENT[exam.accentColor] ?? "notStarted";
      summary[bucket] += 1;
      return summary;
    },
    {
      total: exams.length,
      passed: 0,
      failed: 0,
      inProgress: 0,
      notStarted: 0,
    },
  );
}

function clearResultFields(row) {
  return {
    ...row,
    ...Object.fromEntries(RESULT_FIELDS.map((field) => [field, null])),
  };
}

function copyExamResultToCourse(courseRow, examRow) {
  return {
    ...courseRow,
    accentColor: examRow.accentColor,
    isGy25FinalExam: examRow.isGy25FinalExam,
    ...Object.fromEntries(
      RESULT_FIELDS.map((field) => [field, examRow[field]]),
    ),
  };
}

function addRollupToCourse(courseRow, rollup) {
  return {
    ...clearResultFields(courseRow),
    examTotal: rollup.total,
    examPassed: rollup.passed,
    examFailed: rollup.failed,
    examInProgress: rollup.inProgress,
    examNotStarted: rollup.notStarted,
  };
}

export function buildExamSubset(rows = []) {
  const { byId, childrenByParent } = buildRowIndex(rows);
  const keep = new Set();

  rows.forEach((row) => {
    if (!row.isFinalExam) return;

    keep.add(row.id);
    collectDescendants(row.id, childrenByParent).forEach((descendant) => {
      keep.add(descendant.id);
    });
    collectAncestorIds(row, byId).forEach((id) => keep.add(id));
  });

  return rows.filter((row) => keep.has(row.id));
}

export function collapseGy25Containers(rows = []) {
  const childrenByParent = groupChildrenByParent(rows);
  const containerParentById = new Map(
    rows
      .filter((row) => row.isFinalExam && childrenByParent.has(row.id))
      .map((row) => [row.id, row.parentId]),
  );

  return rows
    .filter((row) => !containerParentById.has(row.id))
    .map((row) =>
      containerParentById.has(row.parentId)
        ? { ...row, parentId: containerParentById.get(row.parentId) }
        : row,
    );
}

export function hoistSingleExams(rows = []) {
  const childrenByParent = groupChildrenByParent(rows);
  const hoistByCourse = new Map();
  const rollupByCourse = new Map();
  const idsToDrop = new Set();

  rows.forEach((row) => {
    if (!isRootRow(row)) return;

    const descendants = collectDescendants(row.id, childrenByParent);
    const exams = descendants.filter(
      (descendant) => !descendant.isDerivedParent,
    );
    if (exams.length === 0) return;

    if (exams.length === 1) {
      hoistByCourse.set(row.id, exams[0]);
      descendants.forEach((descendant) => idsToDrop.add(descendant.id));
    } else {
      rollupByCourse.set(row.id, summariseExams(exams));
    }
  });

  return rows
    .filter((row) => !idsToDrop.has(row.id))
    .map((row) => {
      if (hoistByCourse.has(row.id)) {
        return copyExamResultToCourse(row, hoistByCourse.get(row.id));
      }

      if (rollupByCourse.has(row.id)) {
        return addRollupToCourse(row, rollupByCourse.get(row.id));
      }

      return row;
    });
}

export function buildExamTableData(rows = []) {
  return hoistSingleExams(collapseGy25Containers(buildExamSubset(rows)));
}
